/**
 * Resolve and download conversation Outputs (CDN files or Work artifacts).
 * Kept free of React so unit tests can cover naming / URL selection.
 */

import { downloadMedia } from '@/constants/utils/mediaDownload';
import {
  getFileDisplayName,
  outputFilePublicUrl,
  outputWorkArtifactPath,
  outputWorkTaskId,
  zipEntryNameForOutput,
} from '@/constants/utils/sources_outputs';

function coworkerApi() {
  return require('@/lib/api').coworkerApi;
}

export async function resolveOutputFileUrl(file, { taskId } = {}) {
  const direct = outputFilePublicUrl(file);
  if (direct) return direct;
  const path = outputWorkArtifactPath(file);
  const workId = outputWorkTaskId(file, taskId);
  if (!path || !workId) return null;
  try {
    return await coworkerApi().getArtifactDownloadUrl(workId, path);
  } catch {
    return null;
  }
}

export async function hydrateOutputFileForOpen(file, { taskId } = {}) {
  if (!file) return file;
  if (outputFilePublicUrl(file)) return file;
  const url = await resolveOutputFileUrl(file, { taskId });
  if (url) return { ...file, cdnPath: url, url };
  const path = outputWorkArtifactPath(file);
  const workId = outputWorkTaskId(file, taskId);
  if (!path || !workId || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('Open failed');
  }
  const response = await coworkerApi().fetchArtifactBinary(workId, path, { asProxy: true });
  if (!response?.ok) throw new Error('Open failed');
  const objectUrl = URL.createObjectURL(await response.blob());
  // Previews consume the URL immediately; keep it alive long enough for media
  // metadata/readers to load, then release the browser allocation.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return { ...file, cdnPath: objectUrl, url: objectUrl };
}

async function fetchOutputBlob(file, { taskId } = {}) {
  const url = await resolveOutputFileUrl(file, { taskId });
  if (url) {
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
      if (response.ok) return response.blob();
    } catch {
      /* fall through to the authenticated Work proxy */
    }
  }
  const path = outputWorkArtifactPath(file);
  const workId = outputWorkTaskId(file, taskId);
  if (!path || !workId) throw new Error('Download failed');
  const response = await coworkerApi().fetchArtifactBinary(workId, path, { asProxy: true });
  if (!response?.ok) throw new Error('Download failed');
  return response.blob();
}

function bytesToBase64(bytes) {
  const view = new Uint8Array(bytes);
  const slice = 0x8000;
  let binary = '';
  for (let i = 0; i < view.length; i += slice) {
    binary += String.fromCharCode.apply(null, view.subarray(i, i + slice));
  }
  return btoa(binary);
}

export async function downloadConversationOutput(file, { taskId, ...options } = {}) {
  const filename = getFileDisplayName(file);
  const url = await resolveOutputFileUrl(file, { taskId });
  if (url) return downloadMedia(url, filename, options);
  const blob = await fetchOutputBlob(file, { taskId });
  if (typeof document === 'undefined') {
    const FileSystem = await import('expo-file-system');
    const Sharing = await import('expo-sharing');
    const safeName = filename.replace(/[^A-Za-z0-9._-]/g, '_');
    const fileUri = `${FileSystem.cacheDirectory}${safeName}`;
    const base64 = bytesToBase64(await blob.arrayBuffer());
    await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: FileSystem.EncodingType.Base64 });
    if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(fileUri);
    options.onSuccess?.('Download complete');
    return { success: true, message: 'Download complete' };
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    options.onSuccess?.('Download complete');
    return { success: true, message: 'Download complete' };
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  }
}

export async function downloadConversationOutputsZip(files, {
  taskId,
  zipName = 'outputs.zip',
  onProgress,
} = {}) {
  const list = (Array.isArray(files) ? files : []).filter(Boolean);
  if (!list.length) return { success: false, message: 'No files' };

  if (list.length === 1 || typeof document === 'undefined') {
    let failed = 0;
    for (let i = 0; i < list.length; i += 1) {
      onProgress?.({ done: i, total: list.length });
      try {
        await downloadConversationOutput(list[i], { taskId });
      } catch {
        failed += 1;
      }
    }
    onProgress?.({ done: list.length, total: list.length });
    if (failed === list.length) throw new Error('Download failed');
    return {
      success: true,
      failed,
      message: failed ? `${failed} of ${list.length} files could not be downloaded` : 'Download complete',
    };
  }

  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const used = new Set();
  let failed = 0;

  for (let i = 0; i < list.length; i += 1) {
    onProgress?.({ done: i, total: list.length });
    const file = list[i];
    try {
      const blob = await fetchOutputBlob(file, { taskId });
      zip.file(zipEntryNameForOutput(file, used), blob);
    } catch {
      failed += 1;
    }
  }

  if (failed === list.length) {
    throw new Error('Download failed');
  }

  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = zipName.endsWith('.zip') ? zipName : `${zipName}.zip`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  }
  onProgress?.({ done: list.length, total: list.length });
  return {
    success: true,
    failed,
    message: failed ? `${failed} of ${list.length} files could not be downloaded` : 'Download complete',
  };
}
